import { motion } from 'motion/react'
import { Button } from '@/components/ui/Button'
import { ArrowLeft, Check } from 'lucide-react'
import type { FormData } from '@/hooks/useSubmissionWizard'
import { cn } from '@/lib/utils'
import { GRADES } from '@/lib/constants'

interface GradeStepProps {
  formData: FormData
  updateField: <K extends keyof FormData>(field: K, value: FormData[K]) => void
  onNext: () => void
  onBack: () => void
}

export function GradeStep({ formData, updateField, onNext, onBack }: GradeStepProps) {
  const handleSelect = (grade: string) => {
    updateField('grade', grade)
    // Auto-advance after selection
    setTimeout(onNext, 300)
  }

  return (
    <div className="mx-auto max-w-xl space-y-8">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-3">
        <h2 className="text-foreground font-display text-3xl font-bold md:text-4xl lg:text-5xl">
          What grade are you in?
        </h2>
        <p className="text-foreground-muted text-lg">Select your graduating class</p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="grid grid-cols-2 gap-3"
      >
        {GRADES.map((grade, index) => (
          <motion.button
            key={grade.value}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 + index * 0.03 }}
            onClick={() => handleSelect(grade.value)}
            className={cn(
              'relative rounded-xl border-2 px-4 py-4 text-left transition-all duration-200',
              'hover:border-accent hover:bg-surface-hover',
              formData.grade === grade.value ? 'border-accent bg-accent/10' : 'border-border bg-surface'
            )}
          >
            <span className={cn('font-medium', formData.grade === grade.value ? 'text-accent' : 'text-foreground')}>
              {grade.label}
            </span>
            {formData.grade === grade.value && (
              <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="absolute top-3 right-3">
                <Check className="text-accent h-5 w-5" />
              </motion.div>
            )}
          </motion.button>
        ))}
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
        className="flex items-center gap-4"
      >
        <Button type="button" variant="ghost" size="lg" onClick={onBack}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <span className="text-foreground-subtle text-sm">Tap to select and continue</span>
      </motion.div>
    </div>
  )
}
